<template>
  <div class="notice-form">
    <!-- 返回按钮 -->
    <div class="page-header">
      <el-button @click="handleBack" icon="el-icon-arrow-left"
        >返回列表</el-button
      >
      <h2>{{ pageTitle }}</h2>
    </div>

    <!-- 表单区域 -->
    <div class="form-section">
      <el-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        label-width="120px"
        :disabled="pageMode === 'detail'"
      >
        <el-form-item label="公告标题" prop="title">
          <el-input
            v-model="formData.title"
            placeholder="例如：五一假期活动出行提醒"
            maxlength="50"
            show-word-limit
          />
        </el-form-item>

        <el-form-item label="公告内容" prop="content">
          <div
            v-if="pageMode === 'detail'"
            class="content-preview"
            v-html="formData.content"
          ></div>
          <div v-else>
            <div style="border: 1px solid #ccc">
              <Toolbar
                style="border-bottom: 1px solid #ccc"
                :editor="editor"
                :defaultConfig="toolbarConfig"
                :mode="mode"
              />
              <Editor
                style="height: 400px; overflow-y: hidden"
                v-model="formData.content"
                :defaultConfig="editorConfig"
                :mode="mode"
                @onCreated="onCreated"
              />
            </div>
          </div>
        </el-form-item>

        <el-form-item label="目标人群" prop="targetAudience">
          <el-radio-group v-model="formData.targetAudience">
            <el-radio label="0">全部用户</el-radio>
            <el-radio label="1">普通会员</el-radio>
            <el-radio label="2">领队</el-radio>
            <el-radio label="3">分销商</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item
          label="发布状态"
          prop="publishType"
          v-if="pageMode !== 'detail'"
        >
          <el-radio-group v-model="formData.publishType">
            <el-radio label="immediate">立即发布</el-radio>
            <el-radio label="scheduled">定时发布</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item
          v-if="formData.publishType === 'scheduled' && pageMode !== 'detail'"
          label="定时发布时间"
          prop="publishTime"
        >
          <el-date-picker
            v-model="formData.publishTime"
            type="datetime"
            placeholder="选择发布时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%"
            :picker-options="publishTimePickerOptions"
          />
        </el-form-item>

        <el-form-item label="发布状态" v-if="pageMode === 'detail'">
          <el-tag :type="getStatusType(formData.status)">
            {{ getStatusText(formData.status) }}
          </el-tag>
        </el-form-item>
      </el-form>

      <!-- 详情页显示其他信息 -->
      <div v-if="pageMode === 'detail'" class="detail-info">
        <div class="info-item" v-if="formData.publishTime">
          <span class="label">发布时间：</span>
          <span class="value">{{ formData.publishTime | formatDate }}</span>
        </div>
        <div class="info-item" v-if="formData.createTime">
          <span class="label">创建时间：</span>
          <span class="value">{{ formData.createTime | formatDate }}</span>
        </div>
        <div class="info-item" v-if="formData.updateTime">
          <span class="label">更新时间：</span>
          <span class="value">{{ formData.updateTime | formatDate }}</span>
        </div>
        <div class="info-item" v-if="formData.publishAdminName">
          <span class="label">发布人：</span>
          <span class="value">{{ formData.publishAdminName }}</span>
        </div>
      </div>
    </div>

    <!-- 底部操作按钮 -->
    <div class="footer-actions" v-if="pageMode !== 'detail'">
      <el-button @click="handleBack">取消</el-button>
      <el-button
        type="primary"
        @click="handleSubmit"
        :loading="submitting"
        :disabled="submitting"
        >保存公告</el-button
      >
    </div>
    <div class="footer-actions" v-else>
      <el-button @click="handleBack">返回</el-button>
      <el-button
        type="primary"
        @click="handleEdit"
        v-if="formData.status !== 1"
        :disabled="submitting"
        >编辑</el-button
      >
    </div>
  </div>
</template>

<script>
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import '@wangeditor/editor/dist/css/style.css'
import OSS from 'ali-oss' // 新增：npm i ali-oss

// ===================== OSS 上传工具（模块级 STS 缓存，多组件/多次上传共享）=====================
const OSS_BASE_URL = 'https://img.xunshanke.com/'
let _ossSts = null
let _ossStsExpireAt = 0
let _ossStsRefreshing = null
let _ossClient = null

async function fetchOssSts(force = false) {
  const now = Date.now()
  // 未过期（提前 60s 视为过期）直接复用
  if (!force && _ossSts && _ossStsExpireAt > now + 60 * 1000) {
    return _ossSts
  }
  // 并发去重
  if (_ossStsRefreshing) return _ossStsRefreshing

  _ossStsRefreshing = (async () => {
    try {
      const token = localStorage.getItem('token') || ''
      const res = await fetch('/xunshanke-server/oss/sts.do', {
        headers: { Authorization: token }
      })
      const body = await res.json()
      const ok = ['0', 0, '200', 200].includes(body && body.code)
      if (!ok) throw new Error((body && body.message) || '获取 OSS 凭证失败')

      const sts = body.object || body.result || body.data
      if (!sts || !sts.accessKeyId) throw new Error('OSS 凭证数据异常')

      _ossSts = sts

      // expiration 形如 "2026-09-16T06:53:46Z"
      const rawExp = sts.expiration || sts.expirationTime
      let expire = NaN
      if (typeof rawExp === 'number') expire = rawExp
      else if (rawExp) expire = new Date(rawExp).getTime()
      _ossStsExpireAt = Number.isNaN(expire)
        ? Date.now() + 10 * 60 * 1000
        : expire

      _ossClient = null // 凭证变更，重建 client
      return sts
    } finally {
      _ossStsRefreshing = null
    }
  })()
  return _ossStsRefreshing
}

async function getOssClient(force = false) {
  await fetchOssSts(force)
  if (_ossClient) return _ossClient

  const sts = _ossSts
  const endpoint = sts.endpoint || 'oss-cn-chengdu.aliyuncs.com'
  _ossClient = new OSS({
    region: sts.region || 'oss-cn-chengdu',
    accessKeyId: sts.accessKeyId,
    accessKeySecret: sts.accessKeySecret,
    stsToken: sts.securityToken,
    bucket: sts.bucket,
    endpoint: /^https?:\/\//.test(endpoint) ? endpoint : 'https://' + endpoint,
    secure: true,
    refreshSTSToken: async () => {
      const s = await fetchOssSts(true)
      return {
        accessKeyId: s.accessKeyId,
        accessKeySecret: s.accessKeySecret,
        stsToken: s.securityToken
      }
    },
    refreshSTSTokenInterval: 5 * 60 * 1000
  })
  return _ossClient
}

/**
 * 上传文件到 OSS
 * @param {File} file 原始文件
 * @param {String} dir  目录名，如 'notices' / 'products'
 * @returns {Promise<String>} 可访问的 URL
 */
function uploadToOss(file, dir = 'notices') {
  const name = file.name || ''
  const dotIndex = name.lastIndexOf('.')
  const ext = dotIndex > -1 ? name.substring(dotIndex) : ''
  const objectName =
    `${dir}/` + Date.now() + '_' + Math.random().toString(36).slice(2) + ext

  const doPut = async (force) => {
    const client = await getOssClient(force)
    return client.put(objectName, file)
  }

  return doPut(false)
    .catch(async (error) => {
      // 凭证过期 / 网络抖动：强制刷新 STS 后重试一次
      console.warn('OSS 上传失败，刷新凭证后重试:', error)
      await doPut(true)
    })
    .then(() => OSS_BASE_URL + objectName)
}

export default {
  name: 'NoticeForm',
  components: { Editor, Toolbar },
  data() {
    return {
      editor: null,
      toolbarConfig: {
        MENU_CONF: {}
      },
      editorConfig: {
        placeholder: '请输入内容...',
        MENU_CONF: {
          uploadImage: {
            customUpload(file, insertFn) {
              // 图片 5MB 限制（按需调整）
              const maxSize = 5 * 1024 * 1024
              if (file.size > maxSize) {
                alert('图片大小不能超过 5MB')
                return
              }
              if (!file.type.startsWith('image/')) {
                alert('请上传图片文件')
                return
              }
              uploadToOss(file, 'notices')
                .then((url) => insertFn(url, file.name, url))
                .catch((err) => {
                  console.error('图片上传失败:', err)
                  alert('图片上传失败，请重试')
                })
            }
          },
          uploadVideo: {
            customUpload(file, insertFn) {
              uploadToOss(file, 'notices')
                .then((url) => {
                  // 视频：insertFn(url, poster)
                  insertFn(url, '')
                })
                .catch((err) => {
                  console.error('视频上传失败:', err)
                  alert('视频上传失败，请重试')
                })
            }
          }
        }
      },
      mode: 'default',
      submitting: false,
      publishTimePickerOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7
        },
        disabledHours() {
          const hours = []
          const now = new Date()
          const currentHour = now.getHours()
          if (this.formData && this.formData.publishTime) {
            const selectedDate = new Date(
              this.formData.publishTime.replace(/-/g, '/')
            )
            if (selectedDate.toDateString() === now.toDateString()) {
              for (let i = 0; i <= currentHour; i++) {
                hours.push(i)
              }
            }
          }
          return hours
        },
        disabledMinutes() {
          const minutes = []
          const now = new Date()
          const currentMinute = now.getMinutes()
          if (this.formData && this.formData.publishTime) {
            const selectedDate = new Date(
              this.formData.publishTime.replace(/-/g, '/')
            )
            if (
              selectedDate.toDateString() === now.toDateString() &&
              selectedDate.getHours() === now.getHours()
            ) {
              for (let i = 0; i <= currentMinute; i++) {
                minutes.push(i)
              }
            }
          }
          return minutes
        }
      },
      formData: {
        id: null,
        title: '',
        content: '',
        targetAudience: '0',
        publishType: '',
        publishTime: '',
        status: null,
        createTime: null,
        updateTime: null,
        publishAdminName: ''
      },
      formRules: {
        title: [
          { required: true, message: '请输入公告标题', trigger: 'blur' },
          { max: 50, message: '标题长度不能超过50个字符', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入公告内容', trigger: 'blur' }
        ],
        targetAudience: [
          { required: true, message: '请选择目标人群', trigger: 'change' }
        ],
        publishTime: [
          { required: true, message: '请选择定时发布时间', trigger: 'change' },
          {
            validator: (rule, value, callback) => {
              if (value) {
                const selected = new Date(value.replace(/-/g, '/')).getTime()
                const now = Date.now()
                if (selected <= now) {
                  callback(new Error('定时发布时间不能早于当前时间'))
                } else {
                  callback()
                }
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ]
      }
    }
  },
  computed: {
    pageMode() {
      return this.$route.params.mode || 'create'
    },
    pageTitle() {
      const titleMap = {
        create: '新增公告',
        edit: '编辑公告',
        detail: '公告详情'
      }
      return titleMap[this.pageMode] || '新增公告'
    }
  },
  mounted() {
    if (this.pageMode !== 'create') {
      this.loadDetail()
    }
  },
  beforeDestroy() {
    const editor = this.editor
    if (editor == null) return
    editor.destroy()
  },
  methods: {
    onCreated(editor) {
      this.editor = Object.seal(editor)
    },

    // 将时间戳转为 yyyy-MM-dd HH:mm:ss 格式字符串
    formatTimestamp(value) {
      if (!value) return ''
      let numValue = value
      if (typeof value === 'string') {
        // 如果已经是字符串格式 yyyy-MM-dd HH:mm:ss，直接返回
        if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) {
          return value
        }
        numValue = Number(value)
      }
      if (typeof numValue === 'number' && !isNaN(numValue)) {
        // 秒级时间戳转毫秒
        if (numValue < 1000000000000) {
          numValue = numValue * 1000
        }
        const date = new Date(numValue)
        if (isNaN(date.getTime())) return ''
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const seconds = String(date.getSeconds()).padStart(2, '0')
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
      }
      return ''
    },

    async loadDetail() {
      const id = this.$route.params.id
      if (!id) {
        this.$message.error('缺少公告ID')
        this.handleBack()
        return
      }
      try {
        const res = await this.$http.post('notice/getNoticeDetail.do', { id })
        if (res.code === '0' || res.code === 0) {
          const data = res.result || res.data || {}
          this.formData = {
            id: data.id,
            title: data.title || '',
            content: data.content || '',
            targetAudience: String(data.targetAudience || '0'),
            publishType: data.publishTime ? 'scheduled' : '',
            publishTime: this.formatTimestamp(data.publishTime),
            status: data.status,
            createTime: data.createTime,
            updateTime: data.updateTime,
            publishAdminName: data.publishAdminName || ''
          }
        } else {
          this.$message.error(res.message || '获取公告详情失败')
          this.handleBack()
        }
      } catch (error) {
        console.error('获取公告详情失败:', error)
        this.$message.error('获取公告详情失败')
        this.handleBack()
      }
    },

    handleSubmit() {
      if (this.submitting) return
      this.submitting = true
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) {
          this.submitting = false
          return
        }
        try {
          const params = {
            title: this.formData.title,
            content: this.formData.content,
            targetAudience: this.formData.targetAudience
          }

          let url = ''
          if (this.pageMode === 'edit') {
            params.id = this.formData.id
            url = 'notice/updateNotice.do'
          } else {
            url = 'notice/addNotice.do'
          }

          // 如果选择定时发布，传递publishTime
          if (
            this.formData.publishType === 'scheduled' &&
            this.formData.publishTime
          ) {
            params.publishTime = this.formData.publishTime
          }

          const res = await this.$http.post(url, params)
          if (res.code === '0' || res.code === 0) {
            // 如果选择立即发布，调用发布接口
            if (this.formData.publishType === 'immediate') {
              const noticeId =
                this.pageMode === 'edit'
                  ? this.formData.id
                  : res.data || res.result || res.id
              const publishUrl =
                this.pageMode === 'edit'
                  ? 'notice/publishNow.do'
                  : 'notice/publishNotice.do'
              const publishRes = await this.$http.post(publishUrl, {
                id: noticeId
              })
              if (publishRes.code === '0' || publishRes.code === 0) {
                this.$message.success('发布成功')
              } else {
                this.$message.error(publishRes.message || '发布失败')
              }
            } else {
              this.$message.success(
                this.pageMode === 'edit' ? '更新成功' : '新增成功'
              )
            }
            this.handleBack()
          } else {
            this.$message.error(res.message || '操作失败')
          }
        } catch (error) {
          console.error('保存公告失败:', error)
          this.$message.error('保存失败')
        } finally {
          this.submitting = false
        }
      })
    },

    handleEdit() {
      this.$router.push({
        path: `/notice/edit/${this.formData.id}`
      })
    },

    handleBack() {
      this.$router.push('/notice-management')
    },

    getStatusText(status) {
      const statusMap = { 0: '草稿', 1: '已发布', 2: '已下线' }
      return statusMap[status] || '未知'
    },

    getStatusType(status) {
      const typeMap = { 0: 'info', 1: 'success', 2: 'warning' }
      return typeMap[status] || ''
    }
  },
  filters: {
    formatDate(value) {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'number' || /^\d+$/.test(String(value))) {
        const numValue = typeof value === 'number' ? value : Number(value)
        const date = new Date(numValue)
        if (isNaN(date.getTime())) return '-'
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day} ${hours}:${minutes}`
      }
      if (typeof value === 'string') {
        if (/^\d+$/.test(value.trim())) {
          const date = new Date(Number(value))
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            return `${year}-${month}-${day} ${hours}:${minutes}`
          }
        }
        return value.replace('T', ' ').substring(0, 16)
      }
      return '-'
    }
  }
}
</script>

<style scoped>
.notice-form {
  padding: 20px 24px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 80px);
}

.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.form-section {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.content-preview {
  min-height: 200px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  line-height: 1.8;
}

.detail-info {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.info-item {
  display: flex;
  margin-bottom: 12px;
}

.info-item .label {
  width: 100px;
  flex-shrink: 0;
  color: #6b7280;
  font-size: 14px;
}

.info-item .value {
  flex: 1;
  color: #1f2937;
  font-size: 14px;
}

.footer-actions {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
